import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X, ExternalLink, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchList, createItem, updateItem, deleteItem, uploadFile, resolveMediaUrl } from "../api";

const RESOURCES = {
  wrestlers: {
    label: "Roster",
    columns: ["name", "nickname", "alignment"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "nickname", label: "Nickname", type: "text" },
      { key: "image", label: "Photo", type: "image", required: true },
      { key: "hometown", label: "Hometown", type: "text" },
      { key: "height", label: "Height", type: "text" },
      { key: "weight", label: "Weight", type: "text" },
      { key: "finisher", label: "Finisher", type: "text" },
      { key: "alignment", label: "Alignment", type: "select", options: ["face", "heel"] },
      { key: "affiliation", label: "Affiliation Logo (e.g. WWE ID)", type: "image" },
      { key: "wins", label: "Wins", type: "number" },
      { key: "losses", label: "Losses", type: "number" },
      { key: "order", label: "Sort Order", type: "number" },
      { key: "merch_url", label: "Merch Link (optional)", type: "text" },
      { key: "image_pos", label: "Photo Framing (top / center / bottom)", type: "text" },
      { key: "bio", label: "Bio", type: "textarea" },
    ],
    defaults: { alignment: "face", wins: 0, losses: 0, order: 0 },
  },
  events: {
    label: "Events",
    columns: ["title", "date", "city"],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "date", label: "Date", type: "date", required: true },
      { key: "venue", label: "Venue", type: "text" },
      { key: "city", label: "City", type: "text" },
      { key: "poster", label: "Poster / Logo", type: "image", required: true },
      { key: "poster_fit", label: "Poster Fit (contain / cover)", type: "select", options: ["contain", "cover"] },
      { key: "main_event", label: "Main Event", type: "text" },
      { key: "price_from", label: "Price From", type: "text" },
      { key: "ticket_url", label: "Ticket URL", type: "text" },
      { key: "status", label: "Status", type: "select", options: ["upcoming", "past"] },
      { key: "description", label: "Description", type: "textarea" },
    ],
    defaults: { status: "upcoming", ticket_url: "#" },
  },
  news: {
    label: "News",
    columns: ["title", "category", "author"],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "image", label: "Image", type: "image", required: true },
      { key: "author", label: "Author", type: "text" },
      { key: "excerpt", label: "Excerpt", type: "textarea" },
      { key: "body", label: "Body", type: "textarea" },
    ],
    defaults: { category: "News", author: "CWF Staff" },
  },
  championships: {
    label: "Titles",
    columns: ["title", "champion", "division"],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "image", label: "Belt / Champion Photo", type: "image", required: true },
      { key: "champion", label: "Champion", type: "text", required: true },
      { key: "division", label: "Division", type: "text" },
      { key: "since", label: "Champion Since (YYYY-MM-DD)", type: "text" },
      { key: "reign_days", label: "Reign (days)", type: "number" },
    ],
    defaults: { reign_days: 0 },
  },
  gallery: {
    label: "Gallery",
    columns: ["caption", "event"],
    fields: [
      { key: "image", label: "Image URL", type: "text", required: true },
      { key: "caption", label: "Caption", type: "text" },
      { key: "event", label: "Event", type: "text" },
    ],
    defaults: {},
  },
  videos: {
    label: "Media",
    columns: ["title", "youtube_id", "order"],
    fields: [
      { key: "youtube_id", label: "YouTube URL or Video ID", type: "text", required: true },
      { key: "title", label: "Title (optional)", type: "text" },
      { key: "order", label: "Sort Order", type: "number" },
    ],
    defaults: { order: 0 },
  },
  honorees: {
    label: "Hall of Honor",
    columns: ["name", "order"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "image", label: "Photo", type: "image", required: true },
      { key: "blurb", label: "Blurb", type: "textarea" },
      { key: "order", label: "Sort Order", type: "number" },
    ],
    defaults: { order: 0 },
  },
};

function FormModal({ resource, item, onClose, onSaved }) {
  const cfg = RESOURCES[resource];
  const [form, setForm] = useState(() => {
    const base = { ...cfg.defaults };
    cfg.fields.forEach((f) => { base[f.key] = item?.[f.key] ?? cfg.defaults[f.key] ?? (f.type === "number" ? 0 : ""); });
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleUpload = async (key, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setUploadingKey(key);
    try {
      const { url } = await uploadFile(file);
      set(key, url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      cfg.fields.forEach((f) => { if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0; });
      if (item?.id) await updateItem(resource, item.id, payload);
      else await createItem(resource, payload);
      toast.success(item?.id ? "Updated" : "Created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-form-modal">
      <div className="bg-[#111] border border-zinc-800 w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-zinc-800 sticky top-0 bg-[#111]">
          <h3 className="font-display text-3xl uppercase">{item?.id ? "Edit" : "New"} {cfg.label}</h3>
          <button data-testid="admin-form-close" onClick={onClose} className="text-zinc-400 hover:text-white"><X /></button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {cfg.fields.map((f) => (
            <div key={f.key} className={f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{f.label}{f.required && " *"}</label>
              {f.type === "textarea" ? (
                <textarea data-testid={`field-${f.key}`} rows={4} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)}
                  className="w-full mt-1 bg-[#0a0a0a] border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-brand" />
              ) : f.type === "image" ? (
                <div className="mt-1 flex flex-col sm:flex-row gap-4">
                  <div className="w-28 h-28 shrink-0 border border-zinc-800 bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
                    {form[f.key] ? (
                      <img src={resolveMediaUrl(form[f.key])} alt="preview" className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-zinc-700 text-xs font-mono">No image</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hi text-black font-display text-lg uppercase px-4 py-2 cursor-pointer transition-colors w-fit">
                      <Upload size={16} /> {uploadingKey === f.key ? "Uploading..." : "Upload Image"}
                      <input type="file" accept="image/*" data-testid={`upload-${f.key}`} className="hidden"
                        onChange={(e) => handleUpload(f.key, e.target.files)} disabled={uploadingKey === f.key} />
                    </label>
                    <input data-testid={`field-${f.key}`} type="text" value={form[f.key]} onChange={(e) => set(f.key, e.target.value)}
                      placeholder="…or paste an image URL"
                      className="w-full bg-[#0a0a0a] border border-zinc-800 text-white px-3 py-2 text-sm focus:outline-none focus:border-brand" />
                  </div>
                </div>
              ) : f.type === "select" ? (
                <select data-testid={`field-${f.key}`} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)}
                  className="w-full mt-1 bg-[#0a0a0a] border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-brand capitalize">
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input data-testid={`field-${f.key}`} type={f.type} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)}
                  className="w-full mt-1 bg-[#0a0a0a] border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-brand" />
              )}
            </div>
          ))}
        </div>
        <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
          <button onClick={onClose} className="border border-zinc-700 text-zinc-300 font-display text-xl uppercase px-6 py-2 hover:border-white">Cancel</button>
          <button data-testid="admin-form-save" onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hi disabled:opacity-60 text-black font-display text-xl uppercase px-8 py-2">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkGalleryModal({ onClose, onSaved }) {
  const [files, setFiles] = useState([]);
  const [event, setEvent] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const upload = async () => {
    if (!files.length) return;
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const { url } = await uploadFile(files[i]);
        await createItem("gallery", { image: url, caption: "", event });
        ok++;
      } catch (e) {
        // continue with the rest
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setBusy(false);
    toast.success(`Uploaded ${ok} of ${files.length} photo${files.length > 1 ? "s" : ""}`);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="bulk-gallery-modal">
      <div className="bg-[#111] border border-zinc-800 w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <h3 className="font-display text-3xl uppercase">Bulk Upload Photos</h3>
          <button data-testid="bulk-close" onClick={onClose} className="text-zinc-400 hover:text-white"><X /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Event / Album (applied to all)</label>
            <input data-testid="bulk-event" type="text" value={event} onChange={(e) => setEvent(e.target.value)}
              placeholder="e.g. Thursday Night Throwdown · May 21, 2026"
              className="w-full mt-1 bg-[#0a0a0a] border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-brand" />
          </div>
          <div>
            <label className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hi text-black font-display text-lg uppercase px-4 py-2 cursor-pointer transition-colors w-fit">
              <Upload size={16} /> Choose Photos
              <input type="file" accept="image/*" multiple data-testid="bulk-file-input" className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files))} disabled={busy} />
            </label>
            {files.length > 0 && <p className="text-zinc-400 text-sm mt-2">{files.length} photo{files.length > 1 ? "s" : ""} selected</p>}
          </div>
          {busy && (
            <div>
              <div className="h-2 bg-zinc-800 overflow-hidden">
                <div className="h-full bg-brand transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
              <p className="text-zinc-400 text-xs font-mono mt-2">Uploading {progress.done} / {progress.total}...</p>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="border border-zinc-700 text-zinc-300 font-display text-xl uppercase px-6 py-2 hover:border-white disabled:opacity-50">Cancel</button>
          <button data-testid="bulk-upload-submit" onClick={upload} disabled={busy || !files.length}
            className="bg-brand hover:bg-brand-hi disabled:opacity-60 text-black font-display text-xl uppercase px-8 py-2">
            {busy ? "Uploading..." : `Upload ${files.length || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("wrestlers");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () => {
    setLoading(true);
    fetchList(tab).then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(load, [tab]);

  const onSaved = () => { setEditing(undefined); load(); };
  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await deleteItem(tab, id); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const doLogout = async () => { await signOut(); navigate("/admin/login", { replace: true }); };

  const cfg = RESOURCES[tab];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0a0a0a]">
      <header className="border-b border-zinc-800 bg-[#050505] sticky top-0 z-40">
        <div className="px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-brand text-black font-display text-xl px-2 py-0.5 leading-none">CWF</span>
            <span className="font-display text-2xl uppercase tracking-wide">Control Room</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white flex items-center gap-1 font-mono text-xs uppercase"><ExternalLink size={14} /> View Site</a>
            <span className="text-zinc-500 font-mono text-xs hidden sm:block">{user?.email}</span>
            <button data-testid="admin-logout" onClick={doLogout} className="flex items-center gap-1 text-zinc-300 hover:text-brand font-display text-lg uppercase"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-10 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {Object.entries(RESOURCES).map(([key, r]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`font-display text-xl uppercase tracking-wide px-5 py-2 border-2 transition-colors ${tab === key ? "bg-brand border-brand text-black" : "border-zinc-800 text-zinc-400 hover:border-white hover:text-white"}`}>
              {r.label}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-4xl uppercase tracking-tight">{cfg.label} <span className="text-zinc-600 text-2xl">({items.length})</span></h2>
          <div className="flex gap-3">
            {tab === "gallery" && (
              <button data-testid="admin-bulk-upload" onClick={() => setBulkOpen(true)} className="border-2 border-brand text-brand hover:bg-brand hover:text-black font-display text-xl uppercase px-6 py-2 flex items-center gap-2 transition-colors"><Upload size={18} /> Bulk Upload</button>
            )}
            <button data-testid="admin-add-new" onClick={() => setEditing(null)} className="bg-brand hover:bg-brand-hi text-black font-display text-xl uppercase px-6 py-2 flex items-center gap-2"><Plus size={18} /> Add New</button>
          </div>
        </div>

        {loading ? (
          <p className="text-zinc-500 font-display text-2xl animate-pulse">Loading...</p>
        ) : (
          <div className="border border-zinc-800 overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#111] border-b border-zinc-800">
                <tr>
                  {cfg.columns.map((c) => <th key={c} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 px-4 py-3 capitalize">{c.replace("_", " ")}</th>)}
                  <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-widest text-zinc-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} data-testid={`admin-row-${it.id}`} className="border-b border-zinc-800/60 hover:bg-[#111]">
                    {cfg.columns.map((c) => <td key={c} className="px-4 py-3 text-zinc-200 capitalize">{String(it[c] ?? "—")}</td>)}
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button data-testid={`admin-edit-${it.id}`} onClick={() => setEditing(it)} className="text-zinc-400 hover:text-white p-2 border border-zinc-800 hover:border-white"><Pencil size={15} /></button>
                        <button data-testid={`admin-delete-${it.id}`} onClick={() => remove(it.id)} className="text-zinc-400 hover:text-brand p-2 border border-zinc-800 hover:border-brand"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={cfg.columns.length + 1} className="px-4 py-8 text-center text-zinc-500">No items yet. Add your first one.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing !== undefined && (
        <FormModal resource={tab} item={editing} onClose={() => setEditing(undefined)} onSaved={onSaved} />
      )}
      {bulkOpen && (
        <BulkGalleryModal onClose={() => setBulkOpen(false)} onSaved={() => { setBulkOpen(false); load(); }} />
      )}
    </div>
  );
}
